/**
 * Unified Mesh context menu — combines Vertex / Edge / Face actions.
 *
 * From the header button the full menu opens with all three sections.
 * Ctrl+V / Ctrl+E / Ctrl+F open only the corresponding section.
 * Operators that require a minimum selection are omitted when the selection
 * is too small (Blender convention).
 */

import { useCallback, useMemo } from "react";
import { useStore } from "../app/store.ts";
import { useOperatorContext } from "../app/useOperatorContext.ts";
import {
  activeEditSelectionCount,
  canBevelSelection,
  canBridgeEdgeLoopsSelection,
  canConnectVertexPathSelection,
  canExtrudeSelection,
  canFillSelection,
  canGridFillSelection,
  canInsetSelection,
  canSeparateSelection,
  type OperatorContext,
} from "../modeling/operators.ts";
import type { CommandMenuOption } from "./FloatingCommandMenu.tsx";
import { FloatingCommandMenu } from "./FloatingCommandMenu.tsx";

// ---------------------------------------------------------------------------
// Section builders — each returns the unique options for its section.
// When `dedupe` is true, shared actions (Subdivide, Separate, Select
// Connected) are omitted — they appear once in the common section instead.
// ---------------------------------------------------------------------------

function vertexItems(
  ctx: OperatorContext,
  dedupe: boolean,
): CommandMenuOption[] {
  const items: CommandMenuOption[] = [];
  if (canConnectVertexPathSelection(ctx)) {
    items.push({
      id: "merge_center",
      label: "Merge at Center",
      shortcutKey: "c",
    });
    items.push({ id: "merge_first", label: "Merge at First" });
    items.push({ id: "merge_last", label: "Merge at Last" });
    items.push({
      id: "merge_by_distance",
      label: "Merge by Distance…",
      shortcutKey: "d",
    });
    items.push({ id: "v_sep1", label: "", separator: true });
    items.push({
      id: "connect",
      label: "Connect Vertex Path",
      shortcutKey: "t",
    });
  }
  if (ctx.granularity === "vertex" && canFillSelection(ctx)) {
    if (items.length > 0 && !items[items.length - 1]?.separator) {
      items.push({ id: "v_sep_fill", label: "", separator: true });
    }
    items.push({ id: "fill", label: "Fill", shortcutKey: "f" });
  }
  if (!dedupe) {
    if (ctx.granularity === "vertex" && ctx.selectedVertexCount >= 1) {
      if (items.length > 0 && !items[items.length - 1]?.separator) {
        items.push({ id: "v_sep2", label: "", separator: true });
      }
      items.push({ id: "subdivide", label: "Subdivide", shortcutKey: "s" });
      items.push({ id: "smooth", label: "Smooth Vertices" });
    }
    if (ctx.granularity === "vertex" && ctx.selectedVertexCount >= 3) {
      items.push({ id: "v_sep3", label: "", separator: true });
      items.push({ id: "separate", label: "Separate", shortcutKey: "p" });
    }
    items.push({ id: "v_sep_sel", label: "", separator: true });
    items.push({
      id: "select_connected",
      label: "Select Connected",
      shortcutKey: "l",
    });
  }
  return items;
}

function edgeItems(ctx: OperatorContext, dedupe: boolean): CommandMenuOption[] {
  const items: CommandMenuOption[] = [];
  items.push({
    id: "insert_edge_loop",
    label: "Insert Edge Loop",
    shortcutKey: "i",
  });
  if (ctx.granularity === "edge" && ctx.selectedEdgeCount >= 1) {
    items.push({ id: "edge_slide", label: "Edge Slide" });
    if (!dedupe)
      items.push({ id: "subdivide", label: "Subdivide", shortcutKey: "s" });
  }
  if (canBevelSelection(ctx)) {
    items.push({ id: "e_sep1", label: "", separator: true });
    items.push({ id: "bevel", label: "Bevel", shortcutKey: "b" });
  }
  if (ctx.granularity === "edge" && canFillSelection(ctx)) {
    items.push({ id: "e_sep_fill", label: "", separator: true });
    items.push({ id: "fill", label: "Fill", shortcutKey: "f" });
    if (canGridFillSelection(ctx)) {
      items.push({ id: "grid_fill", label: "Grid Fill" });
    }
    if (canBridgeEdgeLoopsSelection(ctx)) {
      items.push({ id: "bridge_edge_loops", label: "Bridge Edge Loops" });
    }
  }
  if (!dedupe) {
    if (ctx.granularity === "edge" && ctx.selectedEdgeCount >= 1) {
      items.push({ id: "e_sep_smooth", label: "", separator: true });
      items.push({ id: "smooth", label: "Smooth Vertices" });
    }
    if (canSeparateSelection(ctx)) {
      items.push({ id: "e_sep2", label: "", separator: true });
      items.push({ id: "separate", label: "Separate", shortcutKey: "p" });
    }
    items.push({ id: "e_sep_sel", label: "", separator: true });
    items.push({
      id: "select_connected",
      label: "Select Connected",
      shortcutKey: "l",
    });
  }
  return items;
}

function faceItems(ctx: OperatorContext, dedupe: boolean): CommandMenuOption[] {
  const items: CommandMenuOption[] = [];
  if (canInsetSelection(ctx)) {
    items.push({ id: "inset", label: "Inset Faces" });
  }
  if (canExtrudeSelection(ctx)) {
    items.push({ id: "extrude", label: "Extrude Region", shortcutKey: "e" });
    items.push({ id: "f_sep1", label: "", separator: true });
    items.push({ id: "flip_normals", label: "Flip Normals" });
    items.push({
      id: "recalculate_outside",
      label: "Recalculate Outside",
      shortcutKey: "r",
    });
  }
  if (!dedupe) {
    if (ctx.granularity === "face" && ctx.selectedFaceCount >= 1) {
      items.push({ id: "f_sep_smooth", label: "", separator: true });
      items.push({ id: "smooth", label: "Smooth Vertices" });
    }
    if (canSeparateSelection(ctx)) {
      items.push({ id: "f_sep2", label: "", separator: true });
      items.push({ id: "separate", label: "Separate", shortcutKey: "p" });
    }
    items.push({ id: "f_sep_sel", label: "", separator: true });
    items.push({
      id: "select_connected",
      label: "Select Connected",
      shortcutKey: "l",
    });
    if (ctx.granularity === "face" && ctx.selectedFaceCount >= 1) {
      items.push({
        id: "select_similar_normal",
        label: "Select Similar: Normal",
      });
    }
  }
  return items;
}

function commonItems(ctx: OperatorContext): CommandMenuOption[] {
  const items: CommandMenuOption[] = [];
  if (activeEditSelectionCount(ctx) >= 1)
    items.push({ id: "subdivide", label: "Subdivide", shortcutKey: "s" });
  if (activeEditSelectionCount(ctx) >= 1)
    items.push({ id: "smooth", label: "Smooth Vertices" });
  if (canSeparateSelection(ctx))
    items.push({ id: "separate", label: "Separate", shortcutKey: "p" });
  items.push({
    id: "select_connected",
    label: "Select Connected",
    shortcutKey: "l",
  });
  items.push({ id: "clean_up", label: "Clean Up" });
  return items;
}

// ---------------------------------------------------------------------------
// Titles for section-only mode
// ---------------------------------------------------------------------------

const SECTION_TITLES: Record<string, string> = {
  all: "Mesh",
  vertex: "Vertex",
  edge: "Edge",
  face: "Face",
};

// ---------------------------------------------------------------------------
// MeshMenu — unified component
// ---------------------------------------------------------------------------

export function MeshMenu() {
  const open = useStore((s) => s.meshMenuOpen);
  const section = useStore((s) => s.meshMenuSection);
  const setOpen = useStore((s) => s.setMeshMenuOpen);
  const anchorPoint = useStore((s) => s.operatorAnchorPoint);
  const setAnchorPoint = useStore((s) => s.setOperatorAnchorPoint);
  const pushUndo = useStore((s) => s.pushUndo);
  const mergeSelection = useStore((s) => s.mergeSelection);
  const connectVertexPath = useStore((s) => s.connectVertexPath);
  const ctx = useOperatorContext();

  const options = useMemo<CommandMenuOption[]>(() => {
    const items: CommandMenuOption[] = [];
    const showAll = section === "all";

    const vItems =
      showAll || section === "vertex" ? vertexItems(ctx, showAll) : [];
    const eItems = showAll || section === "edge" ? edgeItems(ctx, showAll) : [];
    const fItems = showAll || section === "face" ? faceItems(ctx, showAll) : [];

    if (showAll) {
      const cItems = commonItems(ctx);
      if (cItems.length > 0) {
        items.push(...cItems);
        items.push({ id: "sep_common", label: "", separator: true });
      }
    }

    if (vItems.length > 0) {
      if (showAll)
        items.push({ id: "hdr_vertex", label: "Vertex", sectionHeader: true });
      items.push(...vItems);
    }
    if (eItems.length > 0) {
      if (showAll)
        items.push({ id: "hdr_edge", label: "Edge", sectionHeader: true });
      items.push(...eItems);
    }
    if (fItems.length > 0) {
      if (showAll)
        items.push({ id: "hdr_face", label: "Face", sectionHeader: true });
      items.push(...fItems);
    }

    return items;
  }, [ctx, section]);

  const close = useCallback(() => {
    setOpen(false);
    setAnchorPoint(null);
  }, [setOpen, setAnchorPoint]);

  const handleSelect = useCallback(
    (id: string) => {
      const s = useStore.getState();
      switch (id) {
        // Vertex actions
        case "merge_center":
          mergeSelection("center");
          break;
        case "merge_first":
          mergeSelection("first");
          break;
        case "merge_last":
          mergeSelection("last");
          break;
        case "merge_by_distance":
          s.mergeByDistanceSelection();
          break;
        case "connect":
          connectVertexPath();
          break;
        case "subdivide":
          s.subdivideSelection();
          break;
        case "smooth":
          s.smoothSelection();
          break;
        case "separate":
          s.separateSelection();
          break;
        case "select_connected":
          pushUndo();
          s.selectConnectedFromSelection();
          break;
        case "select_similar_normal":
          pushUndo();
          s.selectSimilarFacesByNormal();
          break;
        case "clean_up":
          s.cleanUpMesh();
          break;

        // Edge actions
        case "bevel":
          s.bevelSelection();
          break;
        case "edge_slide":
          s.enterCommand("edgeSlide");
          break;
        case "insert_edge_loop":
          s.enterEdgeLoopInsert();
          break;
        case "grid_fill":
          s.gridFillSelection();
          break;
        case "bridge_edge_loops":
          s.bridgeEdgeLoops();
          break;

        // Face actions
        case "fill":
          s.fillSelection();
          break;
        case "inset":
          s.insetSelection();
          break;
        case "extrude":
          s.extrudeSelection();
          break;
        case "flip_normals":
          pushUndo();
          s.flipNormals();
          break;
        case "recalculate_outside":
          pushUndo();
          s.recalculateOutside();
          break;
      }
      close();
    },
    [pushUndo, mergeSelection, connectVertexPath, close],
  );

  return (
    <FloatingCommandMenu
      open={open}
      onClose={close}
      title={SECTION_TITLES[section] ?? "Mesh"}
      anchorPoint={anchorPoint}
      options={options}
      onSelect={handleSelect}
      panelKey={`mesh_${section}`}
    />
  );
}
